import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Loader2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { cn } from '../lib/utils';
import { computeTzShiftMs, useTimezoneFormatter } from '../lib/timezone';
import { computeConsumptionXDomain, computeDailyXTicks } from '../lib/chartDomain';
import {
  formatCompact,
  formatGallons,
  formatLcdRead,
  formatSignedGallons,
  formatSignedLcd,
  paddedDomain,
  unitLabel,
} from '../lib/reads';
import type { FlowDataPoint } from '../hooks/useMeterFlow';
import type { MeterEvent } from '../hooks/useMeterEvents';
import type { ReadDataPoint } from '../hooks/useMeterReads';

interface MeterFlowChartProps {
  flow: FlowDataPoint[] | undefined;
  events: MeterEvent[] | undefined;
  reads: ReadDataPoint[] | undefined;
  isLoading: boolean;
  error: Error | null;
  dateRange: { from: Date; to: Date };
  timezoneOverride: string | null;
}

interface ChartDataPoint {
  timestamp: number;
  rate: number | null;
}

const PROCESS_LABELS: Record<string, string> = {
  daily_broadcast: 'Daily Broadcast',
  wave_ingest: 'Wave Ingest',
  get_log: 'Get Log',
  emergency_broadcast: 'Emergency Broadcast',
  wave_emergency_broadcast: 'Wave Emergency Broadcast',
  gateway_ingest: 'Gateway Ingest',
  watersignal_flow: 'WaterSignal Flow',
};

interface FlowTooltipPayload {
  dataKey: string;
  color: string;
  value: number;
  payload: ChartDataPoint;
}

interface FlowTooltipProps {
  active?: boolean;
  payload?: FlowTooltipPayload[];
}

function FlowTooltip({ active, payload }: FlowTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  // Flow timestamps are meter-local time (timezone-naive). No timezone
  // conversion is applied — they display as browser-local time.
  const data = payload[0].payload;

  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
      <p className='text-sm text-muted-foreground mb-1'>
        {new Date(data.timestamp).toLocaleString()}
      </p>
      <p className='font-semibold'>
        {data.rate != null ? `${data.rate.toFixed(3)} gal/min` : 'No reading'}
      </p>
    </div>
  );
}

function TimeTag({ label, isOverride = false }: { label: string; isOverride?: boolean }) {
  return (
    <span
      className={cn(
        'justify-self-start text-xs px-1 py-0.5 rounded font-normal',
        isOverride
          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
          : 'bg-muted text-muted-foreground',
      )}
    >
      {label}
    </span>
  );
}

interface EventTooltipProps {
  event: MeterEvent;
  formatDateTime: (date: Date) => string;
  displayTimezone: string;
  isOverrideActive: boolean;
}

function EventTooltip(
  { event, formatDateTime, displayTimezone, isOverrideActive }: EventTooltipProps,
) {
  const label = PROCESS_LABELS[event.processName] ?? event.processName;

  const rows: { label: string; value: string; tag: string; isOverride: boolean }[] = [
    {
      label: 'Processed',
      value: formatDateTime(new Date(event.updatedAt)),
      tag: displayTimezone,
      isOverride: isOverrideActive,
    },
    ...(event.transmittedAt
      ? [{
        label: 'Transmitted',
        value: new Date(event.transmittedAt).toLocaleString(),
        tag: 'meter time',
        isOverride: false,
      }]
      : []),
    ...(event.dataStart
      ? [{
        label: 'Data start',
        value: new Date(event.dataStart).toLocaleString(),
        tag: 'meter time',
        isOverride: false,
      }]
      : []),
    ...(event.dataEnd
      ? [{
        label: 'Data end',
        value: new Date(event.dataEnd).toLocaleString(),
        tag: 'meter time',
        isOverride: false,
      }]
      : []),
  ];

  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3 text-sm'>
      <div className='flex items-center gap-2 mb-2'>
        <span className='font-semibold'>{label}</span>
        <span className='text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground'>
          {event.updateType === 'consumption_data' ? 'Consumption' : 'Status'}
        </span>
      </div>
      <div className='grid grid-cols-[auto_auto_auto] gap-x-2 gap-y-1 text-muted-foreground items-baseline'>
        {rows.map(({ label: rowLabel, value, tag, isOverride }) => (
          <Fragment key={rowLabel}>
            <span className='font-medium text-foreground text-right'>{rowLabel}:</span>
            <span>{value}</span>
            <TimeTag label={tag} isOverride={isOverride} />
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function formatDateRange(
  range: { from: Date; to: Date },
  timezoneOverride: string | null,
): string {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  if (timezoneOverride) opts.timeZone = timezoneOverride;
  return `${range.from.toLocaleDateString(undefined, opts)} – ${
    range.to.toLocaleDateString(undefined, opts)
  }`;
}

// Chart layout constants matching our Recharts configuration.
// The reads chart (stacked above the flow chart) carries a right-hand Y axis
// for the LCD read. To keep both plots pixel-aligned on the x-axis, the flow
// chart reserves the same right inset even though it has no right axis.
const Y_AXIS_WIDTH = 60;
const RIGHT_AXIS_WIDTH = 60;
const CHART_MARGIN = { left: 5, right: 5 };
// Total horizontal space reserved to the right of both plots.
const RIGHT_INSET = CHART_MARGIN.right + RIGHT_AXIS_WIDTH;
// Pixel distance threshold for snapping to an event line
const HIT_THRESHOLD_PX = 12;
// Strip height (above the plot) where event tick marks live
const EVENT_STRIP_HEIGHT = 22;
// Height of the synced reads chart above the flow chart
const READS_CHART_HEIGHT = 220;

interface EventStripProps {
  eventTimestamps: number[];
  xDomain: [number, number] | undefined;
  hoveredEventIndex: number | null;
}

/**
 * Strip rendered directly above the flow plot. Each event is a thin
 * vertical tick at the same x as it would appear in the chart. Default
 * ticks are muted; the hovered tick is the accent color and slightly
 * taller — preserving the visual cross-reference between the strip and
 * the in-plot hover guide without barcoding the data plot.
 */
function EventStrip({ eventTimestamps, xDomain, hoveredEventIndex }: EventStripProps) {
  if (!xDomain || eventTimestamps.length === 0) {
    return <div style={{ height: EVENT_STRIP_HEIGHT }} aria-hidden />;
  }
  const [dMin, dMax] = xDomain;
  const span = dMax - dMin || 1;

  return (
    <div
      className='relative'
      style={{
        height: EVENT_STRIP_HEIGHT,
        paddingLeft: CHART_MARGIN.left + Y_AXIS_WIDTH,
        paddingRight: RIGHT_INSET,
      }}
      aria-label={`${eventTimestamps.length} process event${
        eventTimestamps.length === 1 ? '' : 's'
      } in range`}
    >
      <div className='relative h-full w-full'>
        {/* Baseline of the strip — anchors the tick marks visually. */}
        <div
          className='absolute left-0 right-0 bottom-0 h-px'
          style={{ backgroundColor: 'hsl(var(--border))' }}
        />
        {eventTimestamps.map((ts, i) => {
          const pct = ((ts - dMin) / span) * 100;
          if (pct < 0 || pct > 100) return null;
          const hovered = hoveredEventIndex === i;
          return (
            <div
              key={i}
              className='absolute bottom-0'
              style={{
                left: `${pct}%`,
                width: hovered ? 2 : 1,
                height: hovered ? EVENT_STRIP_HEIGHT - 4 : EVENT_STRIP_HEIGHT - 8,
                marginLeft: hovered ? -1 : 0,
                backgroundColor: hovered ? 'hsl(var(--chart-3))' : 'hsl(var(--muted-foreground))',
                opacity: hovered ? 1 : 0.5,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ReadChartPoint {
  timestamp: number;
  cumulativeGallons: number | null;
  lcdRead: number | null;
}

interface ReadsTooltipPayload {
  payload: ReadChartPoint;
}

function ReadsTooltip(
  { active, payload, lcdUnit }: {
    active?: boolean;
    payload?: ReadsTooltipPayload[];
    lcdUnit: string;
  },
) {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0].payload;
  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3 text-sm'>
      <p className='text-muted-foreground mb-1'>{new Date(data.timestamp).toLocaleString()}</p>
      <div className='grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5'>
        <span className='text-muted-foreground'>Lifetime read</span>
        <span className='font-mono text-right' style={{ color: 'hsl(var(--primary))' }}>
          {data.cumulativeGallons != null
            ? `${
              data.cumulativeGallons.toLocaleString(undefined, { maximumFractionDigits: 2 })
            } gal`
            : '—'}
        </span>
        <span className='text-muted-foreground'>LCD read</span>
        <span className='font-mono text-right' style={{ color: 'hsl(var(--chart-3))' }}>
          {data.lcdRead != null
            ? `${data.lcdRead.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${lcdUnit}`
            : '—'}
        </span>
      </div>
    </div>
  );
}

function ReadsStatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded-lg border bg-card p-3'>
      <p className='text-xs font-medium text-muted-foreground'>{label}</p>
      <p className='text-lg font-bold mt-0.5 tabular-nums'>{value}</p>
    </div>
  );
}

/**
 * Reads chart stacked directly above the flow plot. It shares the flow chart's
 * x-domain, ticks and right inset so the two plots are pixel-aligned, and draws
 * the same hovered-event guide line so the charts stay visually in sync.
 */
function ReadsSyncChart(
  { data, xDomain, xTicks, hoveredEventTs, lcdUnit }: {
    data: ReadChartPoint[];
    xDomain: [number, number] | undefined;
    xTicks: number[];
    hoveredEventTs: number | null;
    lcdUnit: string;
  },
) {
  // Same fractional padding on both axes so a pure unit difference overlaps.
  const cumulativeDomain = paddedDomain(data.map((d) => d.cumulativeGallons));
  const lcdDomain = paddedDomain(data.map((d) => d.lcdRead));
  return (
    <ResponsiveContainer width='100%' height={READS_CHART_HEIGHT}>
      <LineChart data={data} margin={{ left: CHART_MARGIN.left, right: CHART_MARGIN.right }}>
        <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
        {/* Axis present for domain/scale only; labels live on the flow chart below. */}
        <XAxis
          dataKey='timestamp'
          type='number'
          domain={xDomain}
          // Strictly honor xDomain so the bracket readings (intentionally
          // outside the window) are clipped at the edges instead of stretching
          // the axis — which would misalign this chart with the flow chart.
          allowDataOverflow
          ticks={xTicks}
          tick={false}
          axisLine={false}
          height={1}
        />
        <YAxis
          yAxisId='cumulative'
          width={Y_AXIS_WIDTH}
          tick={{ fontSize: 11 }}
          domain={cumulativeDomain ?? ['auto', 'auto']}
          allowDecimals={false}
          tickFormatter={formatCompact}
          label={{
            value: 'read (gal)',
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))', textAnchor: 'middle' },
          }}
        />
        <YAxis
          yAxisId='lcd'
          orientation='right'
          width={RIGHT_AXIS_WIDTH}
          tick={{ fontSize: 11 }}
          domain={lcdDomain ?? ['auto', 'auto']}
          allowDecimals={false}
          tickFormatter={formatCompact}
          label={{
            value: `LCD${lcdUnit ? ` (${lcdUnit})` : ''}`,
            angle: 90,
            position: 'insideRight',
            style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))', textAnchor: 'middle' },
          }}
        />
        <Tooltip content={<ReadsTooltip lcdUnit={lcdUnit} />} />
        <Line
          yAxisId='cumulative'
          type='linear'
          dataKey='cumulativeGallons'
          stroke='hsl(var(--primary))'
          strokeWidth={1.5}
          // Dot at each actual read sample so it's clear where real values are.
          dot={{ r: 3, strokeWidth: 0, fill: 'hsl(var(--primary))' }}
          connectNulls
        />
        <Line
          yAxisId='lcd'
          type='linear'
          dataKey='lcdRead'
          stroke='hsl(var(--chart-3))'
          strokeWidth={1.5}
          strokeDasharray='4 3'
          dot={{ r: 3, strokeWidth: 0, fill: 'hsl(var(--chart-3))' }}
          connectNulls={false}
        />
        {hoveredEventTs != null && (
          <ReferenceLine
            x={hoveredEventTs}
            yAxisId='cumulative'
            stroke='hsl(var(--chart-3))'
            strokeWidth={1.5}
            strokeOpacity={0.5}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function MeterFlowChart(
  {
    flow,
    events,
    reads,
    isLoading,
    error,
    dateRange,
    timezoneOverride,
  }: MeterFlowChartProps,
) {
  const [hoveredEventIndex, setHoveredEventIndex] = useState<number | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<MeterEvent | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const { displayTimezone, isOverrideActive, formatDateTime } = useTimezoneFormatter(
    timezoneOverride,
  );

  // Flow data is meter time (no timezone) — positions stay unshifted.
  const chartData = useMemo(() => {
    if (!flow || flow.length === 0) return [];

    return flow.map((point) => {
      const date = new Date(point.datetime);
      return {
        timestamp: date.getTime(),
        rate: point.rate,
      };
    });
  }, [flow]);

  // Processed-time event timestamps shifted to the target timezone.
  // Shift is computed per-event so DST transitions are handled correctly.
  const eventTimestamps = useMemo(() => {
    if (!events) return [];
    if (!timezoneOverride) {
      return events.map((e) => new Date(e.updatedAt).getTime());
    }
    return events.map((e) => {
      const d = new Date(e.updatedAt);
      return d.getTime() + computeTzShiftMs(timezoneOverride, d);
    });
  }, [events, timezoneOverride]);

  const xDomain = useMemo(
    () => computeConsumptionXDomain(flow, dateRange.to),
    [flow, dateRange.to],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (eventTimestamps.length === 0 || !chartRef.current || !xDomain) {
        setHoveredEventIndex(null);
        return;
      }

      // Map mouse pixel position → timestamp using known chart layout
      const containerWidth = chartRef.current.clientWidth;
      const plotLeft = CHART_MARGIN.left + Y_AXIS_WIDTH;
      const plotWidth = containerWidth - plotLeft - RIGHT_INSET;
      if (plotWidth <= 0) return;

      const mouseX = e.clientX - chartRef.current.getBoundingClientRect().left;
      const [dMin, dMax] = xDomain;
      const mouseTs = dMin + ((mouseX - plotLeft) / plotWidth) * (dMax - dMin);

      // Convert pixel threshold to timestamp space
      const tsThreshold = (HIT_THRESHOLD_PX / plotWidth) * (dMax - dMin);

      let closestIndex: number | null = null;
      let closestDist = Infinity;

      for (let i = 0; i < eventTimestamps.length; i++) {
        const dist = Math.abs(eventTimestamps[i] - mouseTs);
        if (dist < closestDist && dist < tsThreshold) {
          closestDist = dist;
          closestIndex = i;
        }
      }

      setHoveredEventIndex(closestIndex);
    },
    [eventTimestamps, xDomain],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredEventIndex(null);
  }, []);

  const xTicks = useMemo(() => computeDailyXTicks(xDomain), [xDomain]);

  const hoveredEvent = hoveredEventIndex != null ? events?.[hoveredEventIndex] ?? null : null;
  const hoveredEventTs = hoveredEventIndex != null
    ? eventTimestamps[hoveredEventIndex] ?? null
    : null;

  // Reads (lifetime cumulative + physical LCD), plotted on the same x-domain as
  // the flow chart below. reading_time is meter-local (timezone-naive), like
  // flow.datetime, so the two charts align on the time axis.
  const readsChartData = useMemo<ReadChartPoint[]>(() => {
    if (!reads) return [];
    return reads.map((r) => ({
      timestamp: new Date(r.readingTime).getTime(),
      cumulativeGallons: r.cumulativeGallons,
      lcdRead: r.lcdRead,
    }));
  }, [reads]);

  // Headline stats. Deltas respect the selected range: last in-range reading
  // minus first in-range reading. The series also carries bracket readings just
  // outside the window (inRange = false) to extend the line to the plot edges —
  // those are excluded here.
  const readsStats = useMemo(() => {
    const inRange = reads?.filter((r) => r.inRange) ?? [];
    if (inRange.length === 0) return null;
    const first = inRange[0];
    const last = inRange[inRange.length - 1];
    const unit = unitLabel(last.lcdReadUnit ?? first.lcdReadUnit);
    const lcdDelta = last.lcdRead != null && first.lcdRead != null
      ? last.lcdRead - first.lcdRead
      : null;
    const gallonsDelta = last.cumulativeGallons != null && first.cumulativeGallons != null
      ? last.cumulativeGallons - first.cumulativeGallons
      : null;
    return {
      unit,
      lcdRead: last.lcdRead,
      lifetimeGallons: last.cumulativeGallons,
      lcdDelta,
      gallonsDelta,
    };
  }, [reads]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meter Events</CardTitle>
        <CardDescription>{formatDateRange(dateRange, timezoneOverride)}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className='flex items-center justify-center h-[350px]'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
            <span className='ml-2 text-sm text-muted-foreground'>Loading flow data...</span>
          </div>
        )}

        {error && (
          <div className='flex items-center justify-center h-[350px]'>
            <p className='text-sm text-destructive'>Failed to load flow data</p>
          </div>
        )}

        {!isLoading && !error && chartData.length === 0 && (
          <div className='flex items-center justify-center h-[350px]'>
            <p className='text-sm text-muted-foreground'>No flow data available for this meter</p>
          </div>
        )}

        {!isLoading && !error && chartData.length > 0 && (
          <>
            {readsChartData.length > 0 && readsStats && (
              <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3'>
                <ReadsStatBox
                  label='LCD read'
                  value={formatLcdRead(readsStats.lcdRead, readsStats.unit)}
                />
                <ReadsStatBox
                  label='Lifetime gallons'
                  value={formatGallons(readsStats.lifetimeGallons)}
                />
                <ReadsStatBox
                  label='LCD delta (range)'
                  value={formatSignedLcd(readsStats.lcdDelta, readsStats.unit)}
                />
                <ReadsStatBox
                  label='Gallons delta (range)'
                  value={formatSignedGallons(readsStats.gallonsDelta)}
                />
              </div>
            )}
            {readsChartData.length > 0 && readsStats && (
              <div className='flex flex-wrap items-center gap-4 text-xs mb-1'>
                <span className='font-semibold text-foreground'>Reads</span>
                <div className='flex items-center gap-1.5'>
                  <div
                    className='w-4 h-0 border-t-2'
                    style={{ borderColor: 'hsl(var(--primary))' }}
                  />
                  <span className='text-muted-foreground'>Lifetime read (gal)</span>
                </div>
                <div className='flex items-center gap-1.5'>
                  <div
                    className='w-4 h-0 border-t-2 border-dashed'
                    style={{ borderColor: 'hsl(var(--chart-3))' }}
                  />
                  <span className='text-muted-foreground'>
                    LCD read{readsStats.unit ? ` (${readsStats.unit})` : ''}
                  </span>
                </div>
              </div>
            )}
            <div
              ref={chartRef}
              className='relative'
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              {readsChartData.length > 0 && (
                <ReadsSyncChart
                  data={readsChartData}
                  xDomain={xDomain}
                  xTicks={xTicks}
                  hoveredEventTs={hoveredEventTs}
                  lcdUnit={readsStats?.unit ?? ''}
                />
              )}
              <EventStrip
                eventTimestamps={eventTimestamps}
                xDomain={xDomain}
                hoveredEventIndex={hoveredEventIndex}
              />
              <ResponsiveContainer width='100%' height={350}>
                <AreaChart
                  data={chartData}
                  margin={{ left: CHART_MARGIN.left, right: RIGHT_INSET }}
                >
                  <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                  <XAxis
                    dataKey='timestamp'
                    type='number'
                    domain={xDomain}
                    ticks={xTicks}
                    tickFormatter={(ts) =>
                      new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    width={60}
                    label={{
                      value: 'gal/min',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' },
                    }}
                  />
                  <Tooltip content={<FlowTooltip />} />
                  <Area
                    type='linear'
                    dataKey='rate'
                    stroke='hsl(var(--primary))'
                    strokeWidth={1.5}
                    fill='hsl(var(--primary))'
                    fillOpacity={0.08}
                    dot={false}
                    connectNulls={false}
                  />
                  {
                    /* Hover-only guide line at the selected event's x position.
                      Default-state event marks live in the strip above the plot. */
                  }
                  {hoveredEventIndex != null && eventTimestamps[hoveredEventIndex] != null && (
                    <ReferenceLine
                      x={eventTimestamps[hoveredEventIndex]}
                      stroke='hsl(var(--chart-3))'
                      strokeWidth={1.5}
                      strokeOpacity={0.5}
                    />
                  )}
                  {/* Data range highlight (meter time — not shifted) */}
                  {hoveredEvent?.dataStart && hoveredEvent?.dataEnd && (
                    <ReferenceArea
                      x1={new Date(hoveredEvent.dataStart).getTime()}
                      x2={new Date(hoveredEvent.dataEnd).getTime()}
                      fill='hsl(var(--chart-3))'
                      fillOpacity={0.12}
                      stroke='hsl(var(--chart-3))'
                      strokeOpacity={0.3}
                      ifOverflow='hidden'
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>

              {/* Event metadata tooltip */}
              {hoveredEvent && (
                <div className='absolute top-2 right-2 z-10 pointer-events-none'>
                  <EventTooltip
                    event={hoveredEvent}
                    formatDateTime={formatDateTime}
                    displayTimezone={displayTimezone}
                    isOverrideActive={isOverrideActive}
                  />
                </div>
              )}
            </div>

            <div className='flex flex-wrap gap-4 mt-4 text-xs'>
              <div className='flex items-center gap-1.5'>
                <div
                  className='w-4 h-0 border-t-2'
                  style={{ borderColor: 'hsl(var(--primary))' }}
                />
                <span className='text-muted-foreground'>Flow</span>
              </div>
              <div className='flex items-center gap-1.5'>
                <div
                  className='w-px h-3'
                  style={{ backgroundColor: 'hsl(var(--muted-foreground))' }}
                />
                <span className='text-muted-foreground'>Event</span>
              </div>
              <div className='flex items-center gap-1.5'>
                <div
                  className='w-px h-3'
                  style={{ backgroundColor: 'hsl(var(--chart-3))' }}
                />
                <span className='text-muted-foreground'>Hovered event</span>
              </div>
            </div>

            {events && events.length > 0 && (
              <div className='mt-6'>
                <h3 className='text-sm font-semibold mb-2'>Update History</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Process</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>
                        Processed <TimeTag label={displayTimezone} isOverride={isOverrideActive} />
                      </TableHead>
                      <TableHead>
                        Transmitted <TimeTag label='meter time' />
                      </TableHead>
                      <TableHead>
                        Data Window <TimeTag label='meter time' />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((event, i) => {
                      const isHovered = hoveredEventIndex === i;
                      return (
                        <TableRow
                          key={`${event.updatedAt}-${i}`}
                          className={cn('cursor-pointer', isHovered && 'bg-muted')}
                          onClick={() => setSelectedEvent(event)}
                          onMouseEnter={() => setHoveredEventIndex(i)}
                          onMouseLeave={() => setHoveredEventIndex(null)}
                        >
                          <TableCell>
                            {PROCESS_LABELS[event.processName] ?? event.processName}
                          </TableCell>
                          <TableCell>
                            {event.success
                              ? <CheckCircle2 className='h-4 w-4 text-green-500' />
                              : <XCircle className='h-4 w-4 text-red-500' />}
                          </TableCell>
                          <TableCell>
                            <span className='text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground'>
                              {event.updateType === 'consumption_data' ? 'Consumption' : 'Status'}
                            </span>
                          </TableCell>
                          <TableCell>{formatDateTime(new Date(event.updatedAt))}</TableCell>
                          <TableCell>
                            {event.transmittedAt
                              ? new Date(event.transmittedAt).toLocaleString()
                              : <span className='text-muted-foreground'>&mdash;</span>}
                          </TableCell>
                          <TableCell>
                            {event.dataStart && event.dataEnd
                              ? (
                                <>
                                  {new Date(event.dataStart).toLocaleString()}
                                  {' \u2013 '}
                                  {new Date(event.dataEnd).toLocaleString()}
                                </>
                              )
                              : <span className='text-muted-foreground'>&mdash;</span>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                <Dialog
                  open={selectedEvent !== null}
                  onOpenChange={(open) => !open && setSelectedEvent(null)}
                >
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Update Detail</DialogTitle>
                    </DialogHeader>
                    {selectedEvent && (() => {
                      const evtLabel = PROCESS_LABELS[selectedEvent.processName] ??
                        selectedEvent.processName;
                      return (
                        <div className='grid grid-cols-[120px_1fr] gap-y-3 gap-x-4 text-sm'>
                          <span className='text-muted-foreground'>Process</span>
                          <span>{evtLabel}</span>

                          <span className='text-muted-foreground'>Type</span>
                          <span>
                            {selectedEvent.updateType === 'consumption_data'
                              ? 'Consumption'
                              : 'Status'}
                          </span>

                          <span className='text-muted-foreground'>Status</span>
                          <span>
                            {selectedEvent.success
                              ? (
                                <Badge className='bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-100'>
                                  Success
                                </Badge>
                              )
                              : (
                                <Badge className='bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-100'>
                                  Failed
                                </Badge>
                              )}
                          </span>

                          <span className='text-muted-foreground'>Processed at</span>
                          <span>{formatDateTime(new Date(selectedEvent.updatedAt))}</span>

                          <span className='text-muted-foreground'>Transmitted at</span>
                          <span>
                            {selectedEvent.transmittedAt
                              ? new Date(selectedEvent.transmittedAt).toLocaleString()
                              : <span className='text-muted-foreground'>&mdash;</span>}
                          </span>

                          <span className='text-muted-foreground'>Data window</span>
                          <span>
                            {selectedEvent.dataStart && selectedEvent.dataEnd
                              ? (
                                <>
                                  {new Date(selectedEvent.dataStart).toLocaleString()}
                                  {' \u2013 '}
                                  {new Date(selectedEvent.dataEnd).toLocaleString()}
                                </>
                              )
                              : <span className='text-muted-foreground'>&mdash;</span>}
                          </span>

                          {!selectedEvent.success && (
                            <>
                              <span className='text-muted-foreground'>Error</span>
                              <div className='rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 p-3'>
                                <p className='text-sm text-red-800 dark:text-red-300'>
                                  {selectedEvent.errorMessage ??
                                    'No error details available.'}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
